import { useTable } from "@xest-ui/data-table";

import ClearIcon from "@mui/icons-material/Clear";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import {
  Button,
  Checkbox,
  IconButton,
  Input,
  LinearProgress,
  Modal,
  Paper,
  Popover,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import FilterListIcon from "@mui/icons-material/FilterList";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import { Box } from "@mui/system";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useEffect, useMemo, useState } from "react";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import moment from "moment";

export const DefaultTableComponentsFn = ({ rowKey }) => {
  return {
    Modal: ({ props, action }) => {
      return (
        <Modal
          open={props.openState}
          onClose={() => props.onClose(false)}
          title={props.title}
          className={props.className}
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: [
                "manageFilter.modal",
                "exportData.modal",
                "arrangeColumnsModal",
              ].includes(action)
                ? "auto"
                : 600,
              bgcolor: "background.paper",
              boxShadow: 24,
              pt: 2,
              px: 4,
              pb: 3,
              maxHeight: "max(80vh, 600px)",
              overflow: "auto",
            }}
          >
            <Paper>
              {props.title}
              {props.children}
              {props.footer}
            </Paper>
          </Box>
        </Modal>
      );
    },
    Button: ({ props, action }) => {
      if (action === "columnTitle.filter") {
        return (
          <IconButton {...props}>
            <FilterListIcon />
          </IconButton>
        );
      }
      if (
        [
          "columnTitle.sort.asc",
          "columnTitle.sort.desc",
          "columnTitle.sort.remove",
        ].includes(action)
      ) {
        return <IconButton {...props} size="small" />;
      }
      if (action === "pagination.goToLast") {
        return (
          <Button {...props}>
            <SkipNextIcon />
          </Button>
        );
      }
      if (action === "pagination.goNext") {
        return (
          <Button {...props}>
            <NavigateNextIcon />
          </Button>
        );
      }
      if (action === "pagination.goToFirst") {
        return (
          <Button {...props}>
            <SkipPreviousIcon />
          </Button>
        );
      }
      if (action === "pagination.goBack") {
        return (
          <Button {...props}>
            <ChevronLeftIcon />
          </Button>
        );
      }
      if (action === "filters.FilterBtn") {
        return (
          <Button
            {...props}
            startIcon={<ClearIcon onClick={(e) => props.onIconClick?.(e)} />}
            variant="contained"
          />
        );
      }
      if (action === "saveFilter") {
        return <Button {...props} variant="contained" />;
      }
      return <Button {...props} startIcon={props.icon} />;
    },
    DatePicker: ({ props, action }) => {
      return (
        <DateTimePicker
          label="Basic example"
          value={moment(`${props.value}`).format("YYYY-MM-DD hh:mm:ss")}
          onChange={(value) => {
            if (value) {
              const momentdate = moment(value);
              props.onChange?.({
                // @ts-ignore
                target: {
                  value: momentdate.format("YYYY-MM-DD hh:mm:ss"),
                },
              });
            }
          }}
          renderInput={(params) => <TextField {...params} />}
        />
      );
    },
    Dropdown: ({ props, action }) => {
      const [anchorEl, setAnchorEl] = useState(null);
      const [open, setOpen] = useState(!!props.open);
      const uniqueId = useMemo(
        () => "dt-dropdown" + Math.random().toString(),
        []
      );

      useEffect(() => {
        if (!props.open) setAnchorEl(null);
        else setAnchorEl(document.getElementById(uniqueId));
      }, [props.open, uniqueId]);

      useEffect(() => {
        setOpen(!!props.open);
      }, [props.open]);

      const handleClose = () => {
        setOpen(false);
      };

      const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
        setOpen(true);
      };

      if (!open) {
        return (
          <div id={uniqueId} onClick={handleClick}>
            {props.children}
          </div>
        );
      }

      return (
        <div>
          <div id={uniqueId}>
            {action?.startsWith("table.title.") ? (
              <FilterListIcon />
            ) : (
              props.children
            )}
          </div>
          <Popover
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            onClose={handleClose} //doesnt work, how to close MUI element
          >
            <Box
              sx={{
                p: "10px",
              }}
            >
              {props.options.map((eachOption, index) => (
                <Box
                  key={String(eachOption.value) + index}
                  onClick={() =>
                    eachOption.onClick?.(eachOption.value, {
                      setOpenState: setOpen,
                    })
                  }
                >
                  {eachOption.label}
                </Box>
              ))}
            </Box>
          </Popover>
        </div>
      );
    },

    Input: ({ props: { value, ...props }, action }) => {
      if (action === "exportData.modal.checkBox") {
        return (
          <Checkbox
            checked={props.checked}
            onChange={(e) => {
              props.onChange?.(e);
            }}
          />
        );
      }

      if (action === "setFilterValue") {
        return (
          <Input
            value={value}
            type={props.type}
            onChange={(e) => {
              props.onChange?.(e);
            }}
            sx={{
              flexGrow: 1,
            }}
          />
        );
      }
      return (
        <Input
          value={value}
          type={props.type}
          onChange={(e) => {
            props.onChange?.(e);
          }}
        />
      );
    },

    Radio: ({ props }) => <Radio {...props} />,
    Table: ({ props }) => {
      const {
        data: { pageData, loading },
      } = useTable();
      if (!pageData?.length && !loading) {
        return (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 50,
              borderTopWidth: 1,
              borderBottomWidth: 1,
              borderLeftWidth: 0,
              borderRightWidth: 0,
              borderColor: "divider",
              borderStyle: "solid",
            }}
          >
            <Typography variant="h6">No data</Typography>
          </Box>
        );
      }
      const colStyle = (col) => {
        switch (col.fixed) {
          case "left":
            return {
              position: "sticky",
              left: 0,
              zIndex: 1,
              backgroundColor: (theme) => theme.palette.background.default,
            };
          case "right":
            return {
              position: "sticky",
              right: 0,
              zIndex: 1,
              backgroundColor: (theme) => theme.palette.background.default,
            };
          default:
            return {};
        }
      };
      const tableColumns = (
        <>
          <TableHead>
            <TableRow>
              {props.columns.map((eachColumn, index) => (
                <TableCell
                  key={`${
                    String(eachColumn.dataIndex) +
                    index +
                    index +
                    String(eachColumn.dataIndex)
                  }`}
                  sx={{
                    width: eachColumn.width,
                    ...colStyle(eachColumn),
                    backgroundColor: (theme) =>
                      theme.palette.background.default,
                  }}
                >
                  {eachColumn.title}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {props.loading ? (
                <TableCell colSpan={props.columns.length}>
                  <LinearProgress
                    sx={{
                      width: "inherit", // Set the ProgressBar width to fill its container
                      borderRadius: 10, // Optionally set borderRadius
                    }}
                  />
                </TableCell>
              ) : undefined}
            </TableRow>
          </TableHead>
        </>
      );
      const tableBody = (
        <TableBody>
          {pageData?.map((eachRow, rowIndex) => {
            const render = (col) => {
              try {
                return (
                  col.render?.(eachRow, rowIndex) || eachRow[col.dataIndex]
                );
              } catch (err) {
                // eslint-disable-next-line no-console
                console.warn(
                  `Data Table--- there was an error rendering row\n`,
                  eachRow,
                  err
                );
                return "error";
              }
            };
            return (
              <TableRow key={rowKey ? eachRow[rowKey] : rowIndex}>
                {props.columns.map((eachCol, colIndex) => (
                  <TableCell
                    key={colIndex + String(eachCol.dataIndex)}
                    sx={{
                      ...colStyle(eachCol),
                    }}
                  >
                    {render(eachCol)}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      );
      return (
        <TableContainer component={Paper}>
          <Table>
            {tableColumns}
            {tableBody}
          </Table>
        </TableContainer>
      );
    },
  };
};

export const DefaultTableComponents = DefaultTableComponentsFn({});
